import PropTypes from 'prop-types';
import React from 'react';

import Data from '../../data';
import { WrappedDropdown } from '../input';
import LabelWrapper from '../LabelWrapper.component';
import WrappedTooltip from '../overlay/WrappedTooltip.component';

const { PrintOnEnum } = Data;

export class PrintOnField extends React.Component {
	constructor(props) {
		super(props);

		const { options } = props;

		this.state = {
			printOnOptions: this.buildPrintOnOptions(options),
		};
	}

	componentDidUpdate(prevProps) {
		const { options } = this.props;

		if (options !== prevProps.options) {
			this.updatePrintOnOptions();
		}
	}

	/**
	 * Return an array of print on options. Each option will be added its corresponding icon class
	 * @param {object} params
	 * @returns {array}
	 */
	buildPrintOnOptions = options =>
		options.map(option => ({
			...option,
			iconCls: this.buildPrintOnIconCls(option.value),
		}));

	/**
	 * Return the icon classname selector based on the print on value.
	 * @param {PrintOnEnum} printOnValue
	 * @returns {string}
	 */
	buildPrintOnIconCls = printOnValue => {
		switch (printOnValue) {
			case PrintOnEnum.NetStamps:
				return 'netstamps';
			case PrintOnEnum.ShippingLabel812x11:
			case PrintOnEnum.ShippingLabel1200:
			case PrintOnEnum.ShippingLabel512x812:
				return 'shipping-label';
			case PrintOnEnum.Envelope9:
			case PrintOnEnum.Envelope10:
			case PrintOnEnum.EnvelopeA9:
			case PrintOnEnum.Envelope6:
			case PrintOnEnum.EnvelopeA2:
			case PrintOnEnum.Envelope7:
			case PrintOnEnum.Envelope11:
			case PrintOnEnum.Envelope12:
				return 'envelope';
			case PrintOnEnum.MailingLabelCM3610:
			case PrintOnEnum.MailingLabelCM3710:
			case PrintOnEnum.MailingLabelCM3910:
			case PrintOnEnum.MailingLabelCM3930:
			case PrintOnEnum.EnvelopeCM3810:
			case PrintOnEnum.EnvelopeCM3830:
				return 'certified';
			case PrintOnEnum.MailingLabelSDC3110:
			case PrintOnEnum.MailingLabelSDC2200:
			case PrintOnEnum.MailingLabelSDC5030:
				return 'shipping-label'; // use shipping label icon
			case PrintOnEnum.Roll4x6:
			case PrintOnEnum.Roll418x614:
			case PrintOnEnum.Roll2x7:
				return 'roll';
			default:
				return '';
		}
	};

	/**
	 * Notify the caller about the print on selection.
	 * @param {object} data The selected print-on value and input field name.
	 */
	notifyParent = data => {
		const { onChange } = this.props;

		if (onChange) {
			onChange(data);
		}
	};

	/**
	 * Rebuild the print on options when props udpate.
	 */
	updatePrintOnOptions = () => {
		const { options } = this.props;
		const printOnOptions = this.buildPrintOnOptions(options);

		this.setState({
			printOnOptions,
		});
	};

	onChange = ({ target }) => {
		const { name } = this.props;
		const { value } = target;

		this.notifyParent({
			name,
			value,
		});
	};

	/**
	 * Render print on item template: "<icon> <print-on name>".
	 * @param {object}
	 * @returns {ReactElement}
	 */
	renderItemTemplate = data => {
		const { itemTemplate } = this.props;
		const { label, disabled, iconCls, tooltip, value } = data;

		if (itemTemplate) {
			return itemTemplate(data);
		}

		return (
			<>
				<div className={`print-on-item-${value} ${disabled ? 'disabled' : ''}`}>
					<div className="print-on-icon table-cell">
						<span className={iconCls} />
					</div>
					<div className="print-on-title table-cell">
						<span>{label}</span>
					</div>
				</div>
				<WrappedTooltip
					tooltip={tooltip}
					position="right"
					target={`print-on-item-${value}`}
				/>
			</>
		);
	};

	render() {
		const { value, ...restProps } = this.props;
		const { printOnOptions } = this.state;

		return (
			<div className="common-ui-print-on-field">
				<WrappedDropdown
					{...restProps}
					options={printOnOptions}
					value={value}
					panelClassName="print-on-dropdown-panel"
					itemTemplate={this.renderItemTemplate}
					onChange={this.onChange}
				/>
			</div>
		);
	}
}

PrintOnField.propTypes = {
	// Input field name
	name: PropTypes.string,

	// Print-on value
	value: PropTypes.string,

	// An array of print on options to be populated in the dropdown
	options: PropTypes.array,

	// The template of an option item
	itemTemplate: PropTypes.elementType,

	// The handler to handle selection change
	onChange: PropTypes.func.isRequired,
};

export const PrintOnFieldWithLabel = LabelWrapper(
	PrintOnField,
	'common-ui-print-on-field-wrapper'
);
